import { Injectable, OnDestroy } from '@angular/core';
import { interval, Subject, of, EMPTY, from } from 'rxjs';
import { catchError, exhaustMap, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { ConfigurationService } from './configuration.service';
import { LoggingService } from './logging.service';
import { Store } from '@ngrx/store';
import * as ReviewSelectors from '../views/ccm/state/review-page.selectors';
import { FacilityService } from './facility.service';
import { addCorporations } from '../views/rpm/state/rpm-page.actions';

@Injectable({ providedIn: 'root' })
export class TokenValidationService implements OnDestroy {
  private stop$ = new Subject<void>();
  private started = false;

  constructor(
    private readonly authService: AuthService,
    private readonly config: ConfigurationService,
    private readonly logging: LoggingService,
    private readonly store: Store,
    private readonly facilityService: FacilityService,
  ) {}

  start(): void {
    if (this.started) return;
    this.started = true;

    const pollingMs = this.config.data.pollingInterval || 300000; // default 5m

    interval(pollingMs)
      .pipe(
        // run immediately on start 
        takeUntil(this.stop$),
        // prevent overlap if a cycle takes longer than the interval
        exhaustMap(() => this.validateOnce())
      )
      .subscribe();

    // also run the first validation right away
    this.validateOnce().subscribe();
  }

  stop(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private validateOnce() {
    try {
      const claims: any = this.authService.claims;
      const tokenSub: string | undefined = claims?.sub;
      const tokenUsername: string | undefined = claims?.preferred_username;

      // If no valid access token or missing claims skip quietly
      if (!this.authService.isAuthenticated || !tokenSub) {
        this.logging.logTrace('TokenValidation: skipping (no token or missing sub)');
        return of(null);
      }

      // Get current profile snapshot from store
      return this.store.select(ReviewSelectors.selectProfile).pipe(
        take(1),
        switchMap((profile: any) => {
          const profileUsername: string | undefined = profile?.username || profile?.who?.e_mail;
          const mismatch = !profile || !profileUsername || (tokenUsername && profileUsername !== tokenUsername);

          if (!mismatch) {
            return of(null);
          }

          this.logging.logEvent('TokenValidationMismatch', {
            tokenSub,
            tokenUsername,
            profileUsername,
          });

          // Refresh profile, then infrastructure
          return from(this.authService.getProfile()).pipe(
            switchMap(() => this.facilityService.getInfrastructure()),
            tap((res) => {
              const sorted = res?.corporations?.sort((a, b) => a.name?.localeCompare(b.name));
              if (sorted) {
                this.facilityService.buildInfrastructureTree(sorted);
                this.store.dispatch(addCorporations({ corporations: sorted }));
              }
              this.logging.logTrace('TokenValidation: profile and infrastructure refreshed');
            }),
            catchError((err) => {
              // Network or other failures should not surface to UI
              this.logging.logTrace('TokenValidation: refresh failed', { status: err?.status });
              this.logging.logException(err);
              return EMPTY;
            })
          );
        }),
        catchError((err) => {
          this.logging.logTrace('TokenValidation: validation pipeline error');
          this.logging.logException(err);
          return EMPTY;
        })
      );
    } catch (e: any) {
      this.logging.logException(e);
      return of(null);
    }
  }
}


